import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClientService } from 'src/app/services/connections/http-client.service';
import { NavController } from '@ionic/angular';
import { AppService } from 'src/app/services/app/app.service';
import { Storage } from '@ionic/storage';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private auth: BehaviorSubject<AuthState> = new BehaviorSubject<AuthState>(undefined);
    private initialized = false;
    private previousLogin = false;

    constructor(private http: HttpClientService, private nav: NavController, private app: AppService, private storage: Storage) {}

    public async login(email: string, password: string) {
        const res: AuthState = await this.http.post('login', { email, password });
        await this.storage.set('authState', JSON.stringify(res));
        this.http.setSessionId(res.session_id);
        this.auth.next(res);
        this.previousLogin = true;
    }

    public async logout() {
        this.storage.remove('authState');
        this.auth.next(undefined);
        this.previousLogin = true;
        this.nav.navigateRoot('/login');
    }

    public async signup(formValues: { name: string; email: string; phone: string; password }): Promise<string> {
        return this.http.post('register', formValues);
    }

    public async resetPassword(email: string): Promise<any> {
        return this.http.post('reset_password', { email });
    }

    public async changePassword(newPassword: string): Promise<any> {
        return this.http.post('change_password', { password: newPassword });
    }

    public updateUserInfo(userInfo: any): Promise<any> {
        userInfo.uid = this.currentAuth.user.uid;
        return this.http.post('update_user', userInfo, (newProfile: any) => {
            const cur = { ...this.auth.value, user: newProfile };
            this.auth.next(cur);
            return newProfile;
        });
    }

    public sendMail(mailInfo: any): Promise<any> {
        return this.http.post('holler', mailInfo);
    }

    private async checkForExistingSession(): Promise<void> {
        await this.app.applyStoredServer();
        const stateString = await this.storage.get('authState');
        if (stateString) {
            const state: AuthState = JSON.parse(stateString);
            this.http.setSessionId(state.session_id);
            this.auth.next(state);
            this.previousLogin = true;
        } else {
            this.auth.next(undefined);
        }
        this.initialized = true;
    }

    public async waitForInit(): Promise<void> {
        if (!this.initialized) {
            await this.checkForExistingSession();
        }
    }

    public get authState(): Observable<AuthState> {
        return this.auth.asObservable();
    }

    public get currentAuth(): AuthState {
        return this.auth.value;
    }

    public get isAuthenticated(): boolean {
        return this.auth.value ? true : false;
    }

    public get hasPreviousLogin() {
        return this.previousLogin;
    }
}

export interface AuthState {
    session_id: string;
    type: UserType;
    user: User;
}

export interface User {
    uid: string;
    name: string;
    email: string;
    phone: string;
    post_code: string;
}

export enum UserType {
    Admin = 'admin',
    Seller = 'seller',
    Buyer = 'buyer'
}
